import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Select, Spin } from 'antd';
import {
    searchRegions,
    getRegionPath,
    type RegionSimple,
    type RegionQueryMode,
} from '../../services/regionService';
import {
    matchesRegionPathKeyword,
    normalizeRegionSearchText,
    splitRegionSearchText,
} from '../../utils/regionSearch';

const REGION_SEARCH_LIMIT = 100;

type RegionOption = {
    value: number;
    label: string;
    level?: number;
    codeLength?: number;
    code?: string;
    isActive?: boolean;
};

type RegionSelectPrimitiveValue = number | string | null | undefined;
type RegionSelectValue =
    | RegionSelectPrimitiveValue
    | Array<number | string>
    | null;

const regionOptionCache = new Map<number, Pick<RegionOption, 'label' | 'isActive'>>();

const normalizeRegionValue = (value?: number | string | null): number | undefined => {
    if (value === null || value === undefined || value === '') {
        return undefined;
    }

    const parsedValue = typeof value === 'string' ? Number(value) : value;
    return Number.isNaN(parsedValue) ? undefined : parsedValue;
};

const normalizeRegionValues = (
    value: RegionSelectValue,
    multiple: boolean,
): number | number[] | undefined => {
    if (multiple) {
        if (!Array.isArray(value)) {
            const normalizedSingleValue = normalizeRegionValue(
                value as RegionSelectPrimitiveValue,
            );
            return normalizedSingleValue === undefined ? [] : [normalizedSingleValue];
        }

        return value
            .map((item) => normalizeRegionValue(item))
            .filter((item): item is number => item !== undefined);
    }

    return normalizeRegionValue(value as RegionSelectPrimitiveValue);
};

const mergeUniqueOptions = (...groups: RegionOption[][]): RegionOption[] => {
    const merged = new Map<number, RegionOption>();

    groups.flat().forEach((option) => {
        if (!merged.has(option.value)) {
            merged.set(option.value, option);
        }
    });

    return Array.from(merged.values());
};

const shouldPreferOption = (
    candidate: RegionOption,
    current: RegionOption,
    preferredValue?: number,
): boolean => {
    if (preferredValue !== undefined) {
        if (candidate.value === preferredValue && current.value !== preferredValue) {
            return true;
        }
        if (current.value === preferredValue && candidate.value !== preferredValue) {
            return false;
        }
    }

    const candidateLevel = candidate.level ?? Number.POSITIVE_INFINITY;
    const currentLevel = current.level ?? Number.POSITIVE_INFINITY;
    if (candidateLevel !== currentLevel) {
        return candidateLevel > currentLevel;
    }

    const candidateCodeLength = candidate.codeLength ?? 0;
    const currentCodeLength = current.codeLength ?? 0;
    if (candidateCodeLength !== currentCodeLength) {
        return candidateCodeLength > currentCodeLength;
    }

    return candidate.value > current.value;
};

const mergeDisplayOptions = (
    preferredValue: number | undefined,
    ...groups: RegionOption[][]
): RegionOption[] => {
    const mergedByValue = mergeUniqueOptions(...groups);
    const mergedByLabel = new Map<string, RegionOption>();

    mergedByValue.forEach((option) => {
        const existingOption = mergedByLabel.get(option.label);
        if (!existingOption || shouldPreferOption(option, existingOption, preferredValue)) {
            mergedByLabel.set(option.label, option);
        }
    });

    return Array.from(mergedByLabel.values());
};

const getFallbackKeywords = (keyword: string): string[] => {
    const trimmedKeyword = keyword.trim();
    if (!trimmedKeyword) {
        return [];
    }

    const keywords = new Set<string>([trimmedKeyword]);
    const segments = splitRegionSearchText(trimmedKeyword);

    segments.forEach((segment) => {
        keywords.add(segment);
    });

    for (let i = 1; i < segments.length; i += 1) {
        keywords.add(segments.slice(i).join(''));
    }

    if (/^\d+$/.test(trimmedKeyword)) {
        keywords.add(trimmedKeyword.replace(/\s+/g, ''));
    }

    return Array.from(keywords).slice(0, 6);
};

const matchesRegionKeyword = (option: RegionOption, keyword: string): boolean => {
    const normalizedKeyword = normalizeRegionSearchText(keyword);
    if (!normalizedKeyword) {
        return true;
    }

    const label = option.label.toLocaleLowerCase();
    const code = (option.code ?? '').toLocaleLowerCase();
    if (label.includes(normalizedKeyword) || code.includes(normalizedKeyword)) {
        return true;
    }

    if (matchesRegionPathKeyword(option.label, keyword, option.code)) {
        return true;
    }

    return false;
};

const resolveRegionOption = async (
    regionId: number,
    level?: number,
    code?: string,
    mode?: RegionQueryMode,
): Promise<RegionOption> => {
    const cachedOption = regionOptionCache.get(regionId);
    if (cachedOption) {
        return {
            value: regionId,
            label: cachedOption.label,
            level,
            codeLength: code?.length,
            code,
            isActive: cachedOption.isActive,
        };
    }

    try {
        const path = await getRegionPath(
            regionId,
            mode ? { mode } : undefined,
        );
        const label = path.map((region) => region.name).join(' / ') || String(regionId);
        const currentRegion = path[path.length - 1];
        regionOptionCache.set(regionId, {
            label,
            isActive: currentRegion?.is_active,
        });
        return {
            value: regionId,
            label,
            level: level ?? currentRegion?.level,
            codeLength: (code ?? currentRegion?.code)?.length,
            code: code ?? currentRegion?.code,
            isActive: currentRegion?.is_active,
        };
    } catch {
        return {
            value: regionId,
            label: String(regionId),
            level,
            codeLength: code?.length,
            code,
        };
    }
};

const buildRegionOptionFromSearchResult = (region: RegionSimple): RegionOption => {
    const label = region.path_label || region.name || String(region.id);
    regionOptionCache.set(region.id, {
        label,
        isActive: region.is_active,
    });
    return {
        value: region.id,
        label,
        level: region.level,
        codeLength: region.code?.length,
        code: region.code,
        isActive: region.is_active,
    };
};

const renderRegionOptionLabel = (option: RegionOption): React.ReactNode => (
    <>
        {option.label}
        {option.isActive === false ? '（已停用）' : ''}
    </>
);

interface RegionSelectProps {
    value?: RegionSelectValue;
    onChange?: (value: number | number[] | undefined) => void;
    level?: number;
    minLevel?: number;
    leafOnly?: boolean;
    placeholder?: string;
    disabled?: boolean;
    style?: React.CSSProperties;
    allowClear?: boolean;
    multiple?: boolean;
    mode?: RegionQueryMode;
}

export const RegionSelect: React.FC<RegionSelectProps> = ({
    value,
    onChange,
    level,
    minLevel,
    leafOnly = false,
    placeholder = '\u8bf7\u8f93\u5165\u533a\u57df\u540d\u79f0\u3001\u7f16\u7801\u6216\u8def\u5f84\u5173\u952e\u8bcd...',
    disabled = false,
    style,
    allowClear = true,
    multiple = false,
    mode,
}) => {
    const [fetching, setFetching] = useState(false);
    const [options, setOptions] = useState<RegionOption[]>([]);
    const fetchRef = useRef(0);
    const normalizedValue = useMemo(
        () => normalizeRegionValues(value, multiple),
        [multiple, value],
    );
    const selectedValues = useMemo(
        () =>
            Array.isArray(normalizedValue)
                ? normalizedValue
                : normalizedValue === undefined
                  ? []
                  : [normalizedValue],
        [normalizedValue],
    );
    const selectedValuesRef = useRef<number[]>(selectedValues);

    useEffect(() => {
        selectedValuesRef.current = selectedValues;
    }, [selectedValues]);

    const debounceFetcher = useMemo(() => {
        const loadOptions = async (keyword: string) => {
            const fallbackKeywords = getFallbackKeywords(keyword);
            if (fallbackKeywords.length === 0) {
                const currentSelectedValues = selectedValuesRef.current;
                setOptions((prev) => {
                    if (currentSelectedValues.length === 0) {
                        return [];
                    }

                    return prev.filter((option) =>
                        currentSelectedValues.includes(option.value),
                    );
                });
                setFetching(false);
                return;
            }

            fetchRef.current += 1;
            const fetchId = fetchRef.current;
            setFetching(true);

            try {
                const searchedRegionGroups = await Promise.all(
                    fallbackKeywords.map((searchKeyword) =>
                        searchRegions(searchKeyword, {
                            level,
                            minLevel,
                            leafOnly,
                            limit: REGION_SEARCH_LIMIT,
                            ...(mode ? { mode } : {}),
                        }),
                    ),
                );
                if (fetchId !== fetchRef.current) return;

                const searchedRegions = Array.from(
                    new Map(
                        searchedRegionGroups.flat().map((region) => [region.id, region]),
                    ).values(),
                );

                const optionsWithPath = searchedRegions.map(buildRegionOptionFromSearchResult);
                const matchedOptions = optionsWithPath.filter((option) =>
                    matchesRegionKeyword(option, keyword),
                );

                if (fetchId === fetchRef.current) {
                    setOptions((prev) => {
                        const currentSelectedValues = selectedValuesRef.current;
                        const selectedOptions = prev.filter((option) =>
                            currentSelectedValues.includes(option.value),
                        );

                        return mergeDisplayOptions(
                            currentSelectedValues[0],
                            selectedOptions,
                            matchedOptions,
                        );
                    });
                    setFetching(false);
                }
            } catch {
                if (fetchId === fetchRef.current) {
                    setFetching(false);
                }
            }
        };

        let timeout: ReturnType<typeof setTimeout>;
        return (keyword: string) => {
            clearTimeout(timeout);
            if (keyword.trim()) {
                timeout = setTimeout(() => loadOptions(keyword), 500);
            } else {
                const currentSelectedValues = selectedValuesRef.current;
                setOptions((prev) => {
                    if (currentSelectedValues.length === 0) {
                        return [];
                    }

                    return prev.filter((option) =>
                        currentSelectedValues.includes(option.value),
                    );
                });
                setFetching(false);
            }
        };
    }, [leafOnly, level, minLevel, mode]);

    useEffect(() => {
        if (selectedValues.length === 0) {
            return;
        }

        let isCancelled = false;

        Promise.all(
            selectedValues.map((regionId) => resolveRegionOption(regionId, undefined, undefined, mode))
        )
            .then((resolvedOptions) => {
                if (isCancelled) {
                    return;
                }

                setOptions((prev) =>
                    mergeDisplayOptions(
                        selectedValues[0],
                        prev.filter((option) => selectedValues.includes(option.value)),
                        resolvedOptions,
                    ),
                );
            })
            .catch((error) => {
                if (isCancelled) {
                    return;
                }

                console.error('Failed to load region path', error);
                setOptions((prev) => {
                    const missingOptions = selectedValues
                        .filter(
                            (regionId) =>
                                !prev.some((option) => option.value === regionId),
                        )
                        .map((regionId) => ({
                            value: regionId,
                            label: String(regionId),
                        }));

                    return mergeDisplayOptions(
                        selectedValues[0],
                        prev,
                        missingOptions,
                    );
                });
            });

        return () => {
            isCancelled = true;
        };
    }, [mode, selectedValues]);

    return (
        <Select
            mode={multiple ? 'multiple' : undefined}
            showSearch
            labelInValue={false}
            popupMatchSelectWidth={false}
            value={multiple ? selectedValues : normalizedValue}
            placeholder={placeholder}
            style={style || { width: '100%' }}
            defaultActiveFirstOption={false}
            filterOption={false}
            maxTagCount={multiple ? 'responsive' : undefined}
            onSearch={debounceFetcher}
            onChange={(val) => {
                const nextValue = normalizeRegionValues(
                    val as RegionSelectValue,
                    multiple,
                );
                const nextSelectedValues = Array.isArray(nextValue)
                    ? nextValue
                    : nextValue === undefined
                      ? []
                      : [nextValue];
                selectedValuesRef.current = nextSelectedValues;

                if (nextSelectedValues.length === 0) {
                    setOptions([]);
                }
                onChange?.(
                    multiple
                        ? nextSelectedValues
                        : nextSelectedValues[0] ?? undefined,
                );
            }}
            notFoundContent={fetching ? <Spin size="small" /> : null}
            options={options.map((option) => ({
                ...option,
                label: renderRegionOptionLabel(option),
            }))}
            disabled={disabled}
            allowClear={allowClear}
        />
    );
};

export default RegionSelect;
